import type { ChangeEvent, Dispatch, RefObject, SetStateAction } from 'react'
import Slider from 'react-slider'
import React, { forwardRef, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import moduleStyles from './PriceRangeSlider.module.scss'
import type { RangeSliderValue } from '@ui/components/RangeSlider/RangeSlider'
import { Section } from '@components/category/CategorySidePanel/Section'
import { CwsP } from '@components/cws'
import { Input } from '@ui/components'
import { useTranslate } from '@hooks/useTranslation'
import { startTypingTimer } from '@utils/startTypingTimer'

interface SliderProps {
  minAvailablePrice: string
  maxAvailablePrice: string
  minValue: number
  maxValue: number
  resetMin: boolean
  resetMax: boolean
  onChange: (value: RangeSliderValue) => void
}

// export const PriceRangeSlider: FC<SliderProps> =  ({
//   minAvailablePrice,
//   maxAvailablePrice,
//   onChange,
//   resetMin,
//   resetMax,
//   minValue,
//   maxValue,
// })

export const PriceRangeSlider = forwardRef<HTMLElement, SliderProps>(
  ({ minAvailablePrice, maxAvailablePrice, onChange, resetMin, resetMax, minValue, maxValue }, ref) => {
    const gap = 0

    const minRef = useRef(minAvailablePrice)
    const maxRef = useRef('0')
    const [sliderValues, setSliderValues] = useState<number[]>([0, 0])
    const { translate } = useTranslate()

    useEffect(() => {
      setSliderValues([+minAvailablePrice, +maxAvailablePrice])
      if (minAvailablePrice !== minRef.current && parseInt(minAvailablePrice)) {
        minRef.current = minAvailablePrice
      }

      if (maxAvailablePrice !== maxRef.current && parseInt(maxAvailablePrice)) {
        maxRef.current = maxAvailablePrice
      }
    }, [minAvailablePrice, maxAvailablePrice])

    const [sliderOneValueText, setSliderOneValueText] = useState<string | null>(null)
    const [sliderTwoValueText, setSliderTwoValueText] = useState<string | null>(null)

    useEffect(() => {
      resetMin && setSliderValues((prevState) => [+minRef.current, prevState[1]])
    }, [resetMin, minRef.current])

    useEffect(() => {
      resetMax && setSliderValues((prevState) => [prevState[0], +maxRef.current])
    }, [resetMax, maxRef.current])

    const handleChangeInputMinValue = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      if (isNaN(parseFloat(value)) && value !== '') return
      setSliderOneValueText(value)
      if (parseFloat(value) > parseFloat(minRef.current) && parseFloat(value) < sliderValues[1]) {
        startTypingTimer(() => {
          setSliderValues((prevState) => [+event.target.value, prevState[1]])
        })
      } else {
        startTypingTimer(() => {
          setSliderOneValueText(sliderValues[0].toString())
        })
      }
    }
    const handleChangeInputMaxValue = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      if (isNaN(parseFloat(value)) && value !== '') return
      setSliderTwoValueText(event.target.value)
      if (parseFloat(value) < parseFloat(maxRef.current) && parseFloat(value) > sliderValues[0]) {
        startTypingTimer(() => {
          setSliderValues((prevState) => [prevState[0], +event.target.value])
        })
      } else {
        startTypingTimer(() => {
          setSliderTwoValueText(sliderValues[1].toString())
        })
      }
    }
    useEffect(() => {
      onChange({ min: sliderValues[0], max: sliderValues[1] })
    }, [sliderValues])

    return (
      <Section
        title={translate('filters.price') as string}
        open={true}
        // onHeadingClick={setIsPriceRangeSectionOpen.bind(this, !isPriceRangeSectionOpen)}
      >
        <CwsP>
          {translate('search.filter.pricerange.fromto', {
            from: sliderValues[0] || minAvailablePrice,
            to: sliderValues[1] || maxAvailablePrice,
          })}
        </CwsP>
        <Slider
          className={moduleStyles.slider}
          onChange={(value) => {
            setSliderValues(value)
          }}
          max={+maxAvailablePrice}
          min={+minAvailablePrice}
          value={sliderValues}
        />

        <div className="flex flex-row justify-between h-8">
          <Input
            value={sliderOneValueText ?? sliderValues[0].toString()}
            startAdornment={
              <div
                className="flex items-center justify-center pl-2 cursor-pointer"
                onClick={() => {
                  setSliderValues((prevState) => [+minAvailablePrice, prevState[1]])
                }}
              >
                <CwsP hideSpacing weight="500" className="underline">
                  Min
                </CwsP>
              </div>
            }
            className="px-1 text-sm mt-1.5 mb-0 h-8 text-midnight min-price"
            placeholder="Min price"
            wrapperClassName="h-8"
            containerClassName="w-24 h-8"
            onChange={(newValue, event) => handleChangeInputMinValue(event)}
          />
          <Input
            value={sliderTwoValueText ?? sliderValues[1].toString()}
            endAdornment={
              <div
                className="flex items-center justify-center pr-2 cursor-pointer"
                onClick={() => {
                  setSliderValues((prevState) => [prevState[0], +maxAvailablePrice])
                }}
              >
                <CwsP hideSpacing weight="500" className="underline">
                  Max
                </CwsP>
              </div>
            }
            className="px-1 text-right text-sm mt-1.5 mb-0 h-8 w-full text-midnight"
            placeholder="Min price"
            wrapperClassName="h-8"
            containerClassName="w-24 h-8"
            onChange={(newValue, event) => handleChangeInputMaxValue(event)}
          />
        </div>
      </Section>
    )
  }
)
